import React, { useEffect, useState } from "react";
import * as A from "./lib/api";
import { H1, H2, Sub, Card } from "./Pages";
import { BookOpen, Scale, GraduationCap, Landmark, ExternalLink, Search, Loader2 } from "lucide-react";

const GROUPS = [
  { key: "law", label: "Law & Regulation", icon: Scale, sources: "eCFR · Federal Register · CourtListener" },
  { key: "scholarship", label: "Articles & Scholarship", icon: GraduationCap, sources: "Crossref · Europe PMC · DOAJ · OpenAlex" },
  { key: "books", label: "Books", icon: BookOpen, sources: "Open Library · Internet Archive" },
  { key: "funding", label: "Funding", icon: Landmark, sources: "Grants.gov · USAspending" },
];

// ==================== LIVE LIBRARY (federated search) ====================
export function LibraryPage() {
  const [q, setQ] = useState("dynasty trust perpetuities");
  const [active, setActive] = useState({ law: true, scholarship: true, books: true, funding: true });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    const groups = GROUPS.filter(g => active[g.key]).map(g => g.key);
    if (!q.trim() || groups.length === 0) return;
    setBusy(true); setData(null);
    try { const r = await A.librarySearch(q, groups.join(",")); setData(r.data); }
    finally { setBusy(false); }
  };
  useEffect(() => { search(); }, []); // eslint-disable-line

  const toggle = (k) => setActive(a => ({ ...a, [k]: !a[k] }));

  return (
    <div data-testid="library-page">
      <H1>Live Library</H1>
      <Sub>Federated real-time search across official law, open scholarship, books and funding — every source queried concurrently, results render as they return. Partial results are normal when an upstream is slow.</Sub>

      <div className="flex gap-2 mb-3">
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && search()} className="max-w-xl" placeholder="Search the whole library…" data-testid="library-q" />
        <button className="btn-primary flex items-center gap-1" onClick={search} disabled={busy} data-testid="library-search-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Search
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {GROUPS.map(g => (
          <button key={g.key} onClick={() => toggle(g.key)} data-testid={`library-group-${g.key}`}
            className={active[g.key] ? "btn-primary text-xs" : "btn-outline text-xs"}>
            <g.icon size={13} className="inline mr-1" />{g.label}
          </button>
        ))}
      </div>

      {busy && !data && <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--muted)' }}><Loader2 size={16} className="animate-spin" /> Querying live sources…</div>}

      {data && GROUPS.filter(g => active[g.key] && data[g.key]).map(g => {
        const grp = data[g.key];
        return (
          <div key={g.key} className="mb-8" data-testid={`library-results-${g.key}`}>
            <div className="flex items-center justify-between mb-3">
              <H2><g.icon size={18} className="inline mr-2" style={{ verticalAlign: "-2px" }} />{g.label}</H2>
              <div className="text-xs" style={{ color: 'var(--muted)' }}>
                {grp.results.length} results · {g.sources}
                {grp.errors?.length > 0 && <span style={{ color: 'var(--gold-hover)' }}> · {grp.errors.join(", ")} unavailable</span>}
              </div>
            </div>
            {grp.results.length === 0 ? (
              <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>No results returned for this group.</div></Card>
            ) : (
              <div className="space-y-2">
                {grp.results.map((r, i) => (
                  <Card key={`${g.key}-${i}`} className="p-4">
                    <div className="flex justify-between items-start gap-3">
                      <div className="min-w-0">
                        <div className="font-medium" style={{ color: 'var(--primary)' }}>{r.title || "Untitled"}</div>
                        {r.snippet && <div className="text-sm mt-1" style={{ color: 'var(--muted)' }}>{r.snippet}</div>}
                        <div className="text-xs mt-1 flex items-center gap-2">
                          <span className="chip chip-gold text-xs">{r.source}</span>
                          {r.meta && <span style={{ color: 'var(--muted)' }}>{r.meta}</span>}
                        </div>
                      </div>
                      {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="btn-outline text-xs shrink-0 flex items-center gap-1"><ExternalLink size={12} /> Open</a>}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ==================== TAX RESOURCE REFERENCE ====================
export function TaxResourcesPage() {
  const [rows, setRows] = useState(null);
  const [cat, setCat] = useState("");
  useEffect(() => { A.taxResources().then(r => setRows(r.data)); }, []);
  if (!rows) return <div>Loading…</div>;
  const cats = [...new Set(rows.map(r => r.category))];
  const list = rows.filter(r => !cat || r.category === cat);
  const accessTone = (a = "") => a.toLowerCase().includes("free") || a.includes("CONNECTED") ? "green" : a.toLowerCase().includes("tier") ? "gold" : "gray";

  return (
    <div data-testid="tax-resources-page">
      <H1>Tax Resource Reference</H1>
      <Sub>A curated map of the tax-technology landscape for the Grove — sales/use, payroll, estate/gift/trust, regulatory research, and entity-status providers. Free and already-connected sources are flagged; paid vendors are listed for reference, not integrated.</Sub>
      <div className="flex gap-2 mb-4 flex-wrap">
        <button className={!cat ? "btn-primary text-xs" : "btn-outline text-xs"} onClick={() => setCat("")}>All ({rows.length})</button>
        {cats.map(c => <button key={c} className={cat === c ? "btn-primary text-xs" : "btn-outline text-xs"} onClick={() => setCat(c)} data-testid={`tax-cat-${c.split(' ')[0]}`}>{c}</button>)}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {list.map(t => (
          <Card key={t.id} data-testid={`tax-card-${t.id}`}>
            <div className="flex justify-between items-start mb-1">
              <div className="font-display text-lg" style={{ color: 'var(--primary)' }}>{t.name}</div>
              <span className={`chip chip-${accessTone(t.access)} text-xs`}>{t.access}</span>
            </div>
            <div className="text-xs mb-2" style={{ color: 'var(--muted)' }}>{t.category} · {t.coverage}</div>
            <div className="text-sm mb-3">{t.note}</div>
            {t.url && <a href={t.url} target="_blank" rel="noreferrer" className="btn-outline text-xs flex items-center gap-1 w-fit"><ExternalLink size={12} /> Visit</a>}
          </Card>
        ))}
      </div>
    </div>
  );
}
